import { addActor, after, between, budget, rand, spawn } from '../engine';
import { burst, ease, explode, glints, launch, light, pop, rain, shock, wash, type Stage } from '../kit';
import {
  drop,
  envelope,
  firecracker,
  glow,
  glowSprite,
  koi,
  paper,
  petal,
  plum,
  puff,
  redPaper,
  spark,
  stamp,
  tile,
  twinkle,
} from '../shapes';
import { serpent, rays } from './pieces';
import { TAU, UP, type Preset } from './types';

/* ===================================== Carp: waterfall spray, the carp leaps and becomes a dragon ===================================== */

const WATER = ['#4FC3F7', '#8FE3FF', '#EAF8FF', '#2EE6C9'] as const;

function splash(
  s: Stage,
  x: number,
  y: number,
  n: number,
  speed: readonly [number, number] = [200, 460],
): void {
  burst(s, x, y, {
    n,
    speed,
    angle: UP,
    spread: 1.2,
    draw: drop,
    cols: WATER,
    size: [2.5, 5],
    life: [0.7, 1],
    gravity: 950,
    drag: 0.8,
  });
}

function carpLeap(s: Stage): void {
  addActor({
    dur: 1.3,
    front: true,
    draw(g, k, f) {
      const x0 = f.W * 0.38;
      const x1 = f.W * 0.5;
      const yb = f.H + 40;
      const yt = f.H * 0.3;
      const x = x0 + (x1 - x0) * k;
      const y = yb - (yb - yt) * (1 - Math.pow(1 - k, 2));
      const dir = Math.atan2(-(yb - yt) * 2 * (1 - k), x1 - x0);
      g.globalAlpha = 1;
      stamp(g, koi, x, y, Math.min(f.W, f.H) * 0.07, dir, '#F26B3A', '#FFD166', f.dpr, { t: f.t, ph: 0 });
    },
  });
  after(1250, () => {
    const cx = s.W * 0.5;
    const cy = s.H * 0.3;
    const st: Stage = { ...s, x: cx, y: cy };
    wash(cx, cy, { col: '#FFD166', r: Math.max(s.W, s.H) * 0.5, dur: 1.6, alpha: 0.55 });
    shock(cx, cy, { col: '#FFD166', r: Math.min(s.W, s.H) * 0.5, dur: 1, width: 5 });
    glints(st, cx, cy, 30, 60, ['#FFFFFF', '#FFD166', '#FFE7A3']);
    serpent({
      dur: 2.2,
      body: ['#FFD166', '#2EE6C9'],
      aura: '#FFD166',
      width: Math.min(s.W, s.H) * 0.026,
      segments: 30,
      trail: '#FFE7A3',
      dragon: true,
      path: (k, W, H) => {
        const an = k * TAU * 1.3;
        const R = Math.min(W, H) * 0.16 * (1 - k * 0.3);
        return { x: W * 0.5 + Math.sin(an) * R * 1.4, y: H * 0.3 - k * H * 0.55 + Math.cos(an) * R * 0.5 };
      },
    });
  });
}

export const carp: Preset = {
  pal: WATER,
  light: '#FFFFFF',
  win(s) {
    splash(s, s.x, s.y, 30);
    shock(s.x, s.y, { col: '#BFEFFF', r: 60, dur: 0.7, width: 2.5, squash: 0.4 });
    glints(s, s.x, s.y, 6, 24, ['#FFFFFF', '#FFD166']);
  },
  big(s) {
    // the waterfall: a curtain of streaks, mist at its foot, splashes
    rain(s, {
      n: 200,
      over: 1500,
      band: [0.3, 0.7],
      vy: [520, 820],
      draw: spark,
      cols: WATER,
      size: [1.4, 2.8],
      life: [1.2, 1.5],
      gravity: 600,
      face: true,
      alpha: 0.75,
    });
    for (let i = 0; i < budget(10); i++)
      spawn({
        x: s.W * between(0.3, 0.7),
        y: s.H - between(0, 40),
        vx: between(-30, 30),
        vy: -between(10, 40),
        delay: rand() * 0.8,
        draw: glow,
        col: '#BFEFFF',
        size: between(40, 70),
        grow: 0.6,
        life: 1.6,
        alpha: 0.22,
        add: true,
        fadeIn: 0.3,
      });
    for (let i = 0; i < 5; i++) after(i * 260, () => splash(s, s.W * between(0.3, 0.7), s.H, 26, [300, 620]));
    splash(s, s.x, s.y, 36);
    glints(s, s.W / 2, s.H * 0.3, 18, s.W * 0.2, ['#FFFFFF', '#FFD166']);
  },
  mega(s) {
    rain(s, {
      n: 160,
      over: 1300,
      band: [0.3, 0.7],
      vy: [520, 820],
      draw: spark,
      cols: WATER,
      size: [1.4, 2.8],
      life: [1.2, 1.5],
      gravity: 600,
      face: true,
      alpha: 0.7,
    });
    splash(s, s.W * 0.38, s.H, 50, [400, 760]);
    carpLeap(s);
  },
};

/* ===================================== Firecrackers: red paper 红纸, crackles, a hanging garland ===================================== */

const RED = ['#E5484D', '#D93A3F', '#B3262C', '#FF6A5C'] as const;
const FLAME = ['#FFD166', '#FF8A3D', '#FFF3D6'] as const;

function crackle(s: Stage, x: number, y: number, k = 1): void {
  light(s, x, y, 46 * k, '#FFB347', 0.32, 0.9);
  burst(s, x, y, {
    n: Math.round(22 * k),
    speed: [150 * k, 380 * k],
    draw: spark,
    cols: FLAME,
    size: [1.2, 2.2],
    life: [0.35, 0.6],
    drag: 0.3,
    face: true,
    add: true,
  });
  burst(s, x, y, {
    n: Math.round(16 * k),
    speed: [80, 260 * k],
    draw: redPaper,
    cols: RED,
    col2: '#7A1418',
    size: [4, 7],
    life: [0.8, 1.25],
    gravity: 380,
    drag: 0.45,
    flip: [6, 12],
    vr: [-6, 6],
  });
  spawn({
    x,
    y,
    vx: between(-10, 10),
    vy: -20,
    draw: puff,
    col: '#8A8F99',
    size: 22 * k,
    grow: 1.2,
    life: 1,
    alpha: 0.22,
    fadeIn: 0.1,
  });
  pop('firecrackers/firecracker', 0.4, 110);
}

function garland(s: Stage, x: number, n: number, len: number, delay: number): void {
  const step = len / n;
  const start = delay + 0.25;
  const every = 0.085;
  addActor({
    dur: start + n * every + 0.1,
    draw(g, _k, f) {
      const t = f.t;
      const popped = Math.max(0, Math.min(n, Math.floor((t - start) / every) + 1));
      const left = n - popped;
      const appear = ease.out(Math.min(1, t / 0.25));
      g.globalAlpha = 1;
      g.strokeStyle = '#C8912A';
      g.lineWidth = 2;
      g.beginPath();
      g.moveTo(x, 0);
      g.lineTo(x, (left + 0.5) * step * appear);
      g.stroke();
      for (let i = 0; i < left; i++) {
        const y = (i + 0.7) * step * appear;
        const side = i % 2 ? 1 : -1;
        stamp(
          g,
          firecracker,
          x + side * step * 0.45,
          y,
          Math.max(12, step * 0.5),
          side * 1.0,
          '#E5484D',
          '#FFD166',
          f.dpr,
        );
      }
      // the knot on top
      g.fillStyle = '#FFD166';
      g.beginPath();
      g.arc(x, 4, 6, 0, TAU);
      g.fill();
    },
  });
  // pops from the bottom up
  for (let i = n - 1; i >= 0; i--) {
    const j = n - 1 - i;
    after((start + j * every) * 1000, () =>
      crackle(s, x + (i % 2 ? 1 : -1) * step * 0.45, (i + 0.7) * step, 0.85),
    );
  }
}

export const firecrackers: Preset = {
  pal: RED,
  light: '#FFD166',
  win(s) {
    crackle(s, s.x, s.y, 1);
  },
  big(s) {
    for (let i = 0; i < 10; i++)
      after(i * 150, () => crackle(s, s.W * between(0.1, 0.9), s.H * between(0.15, 0.7), 1.4));
    rain(s, {
      n: 120,
      over: 1500,
      vy: [90, 180],
      draw: redPaper,
      cols: RED,
      col2: '#7A1418',
      size: [5, 8],
      life: [2, 2.5],
      sway: [20, 40],
      flip: [4, 9],
      vr: [-3, 3],
      gravity: 60,
    });
    crackle(s, s.x, s.y, 1.2);
  },
  mega(s) {
    const len = s.H * 0.66;
    garland(s, s.W / 2, 21, len, 0);
    if (s.W > 700) {
      garland(s, s.W * 0.2, 13, len * 0.62, 0.35);
      garland(s, s.W * 0.8, 13, len * 0.62, 0.35);
    }
    rain(s, {
      n: 120,
      over: 2400,
      vy: [90, 180],
      draw: redPaper,
      cols: RED,
      col2: '#7A1418',
      size: [5, 8],
      life: [2.2, 2.6],
      sway: [20, 40],
      flip: [4, 9],
      vr: [-3, 3],
      gravity: 60,
    });
    wash(s.W / 2, s.H * 0.4, { col: '#FF8A3D', r: Math.max(s.W, s.H) * 0.5, dur: 3.2, alpha: 0.22 });
  },
};

/* ===================================== Hongbao: a rain of red envelopes, the great envelope opens ===================================== */

const HB = ['#E5484D', '#D93A3F', '#C92F37', '#FF6A5C'] as const;
const GOLD = ['#FFD166', '#FFE7A3', '#F2B33D'] as const;

function greatEnvelope(s: Stage): void {
  let opened = false;
  rays({
    x: (W) => W / 2,
    y: (H) => H * 0.42,
    r: (W, H) => Math.max(W, H) * 0.5,
    cols: GOLD,
    n: 18,
    dur: 2.6,
    delay: 0.45,
    alpha: 0.14,
  });
  addActor({
    dur: 2.4,
    front: true,
    draw(g, k, f) {
      const cx = f.W / 2;
      const cy = f.H * 0.46;
      const w = Math.min(f.W, f.H) * 0.13;
      const h = w * 1.4;
      const a = ease.env(k, 0.08, 0.75);
      const sc = ease.back(ease.span(k, 0, 0.2));
      const flap = ease.inOut(ease.span(k, 0.22, 0.42)); // 0 closed → 1 open
      g.save();
      g.globalAlpha = a;
      g.translate(cx, cy);
      g.scale(sc, sc);
      // a gold card slides out of the opened envelope (behind its front)
      const rise = ease.out(ease.span(k, 0.36, 0.62)) * h * 0.85;
      if (rise > 0) {
        g.fillStyle = '#FFD166';
        g.beginPath();
        g.roundRect?.(-w * 0.82, -h - rise + h * 0.08, w * 1.64, h * 1.3, w * 0.08);
        if (!g.roundRect) g.rect(-w * 0.82, -h - rise + h * 0.08, w * 1.64, h * 1.3);
        g.fill();
        g.strokeStyle = '#F2A81D';
        g.lineWidth = 3;
        g.strokeRect(-w * 0.66, -h - rise + h * 0.2, w * 1.32, h * 0.5);
      }
      g.fillStyle = '#C92F37';
      g.beginPath();
      g.roundRect?.(-w, -h, w * 2, h * 2, w * 0.12);
      if (!g.roundRect) g.rect(-w, -h, w * 2, h * 2);
      g.fill();
      // light from inside
      const sp = glowSprite('#FFD166');
      if (sp && flap > 0) {
        g.globalCompositeOperation = 'lighter';
        g.globalAlpha = a * flap;
        g.drawImage(sp, -w * 1.6, -h * 1.9, w * 3.2, w * 2.4);
        g.globalCompositeOperation = 'source-over';
        g.globalAlpha = a;
      }
      // the flap: folds up (scaleY 1 → -1)
      g.save();
      g.translate(0, -h);
      g.scale(1, 1 - 2 * flap);
      g.fillStyle = flap > 0.5 ? '#A8232A' : '#E5484D';
      g.beginPath();
      g.moveTo(-w, 0);
      g.lineTo(0, h * 0.75);
      g.lineTo(w, 0);
      g.closePath();
      g.fill();
      g.strokeStyle = '#FFD166';
      g.lineWidth = 2;
      g.stroke();
      g.restore();
      if (flap < 0.5) {
        g.fillStyle = '#FFD166';
        g.beginPath();
        g.arc(0, -h + h * 0.72, w * 0.28, 0, TAU);
        g.fill();
      }
      g.restore();
      if (!opened && k > 0.4) {
        opened = true;
        const st: Stage = { ...s, x: cx, y: cy - h };
        glints(st, cx, cy - h, 40, w, ['#FFFFFF', ...GOLD]);
        burst(st, cx, cy - h, {
          n: 26,
          speed: [260, 620],
          angle: UP,
          spread: Math.PI * 1.5,
          draw: envelope,
          cols: HB,
          col2: '#FFD166',
          size: [14, 20],
          life: [1.6, 2.2],
          gravity: 520,
          drag: 0.6,
          flip: [2, 4],
          vr: [-3, 3],
        });
        burst(st, cx, cy - h, {
          n: 60,
          speed: [120, 420],
          draw: [glow, twinkle],
          cols: GOLD,
          size: [3, 7],
          life: [1, 1.6],
          drag: 0.5,
          gravity: 80,
          add: true,
        });
      }
    },
  });
}

export const hongbao: Preset = {
  pal: HB,
  light: '#FFD166',
  win(s) {
    burst(s, s.x, s.y, {
      n: 6,
      speed: [220, 380],
      angle: UP,
      spread: 1.2,
      draw: envelope,
      col2: '#FFD166',
      size: [11, 15],
      life: [0.8, 1],
      gravity: 800,
      drag: 0.7,
      flip: [3, 6],
      vr: [-3, 3],
    });
    glints(s, s.x, s.y, 12, 30, ['#FFFFFF', ...GOLD]);
  },
  big(s) {
    rain(s, {
      n: 46,
      over: 1500,
      vy: [140, 260],
      vx: [-20, 20],
      draw: envelope,
      col2: '#FFD166',
      size: [16, 24],
      life: [2.4, 3],
      sway: [20, 50],
      flip: [1.5, 3.5],
      vr: [-1.5, 1.5],
      gravity: 60,
    });
    rain(s, {
      n: 80,
      over: 1500,
      vy: [60, 140],
      draw: twinkle,
      cols: GOLD,
      size: [3, 6],
      life: [1.6, 2.2],
      vr: [-2, 2],
      add: true,
      sway: [10, 20],
    });
    burst(s, s.x, s.y, {
      n: 10,
      speed: [240, 420],
      angle: UP,
      spread: 1.4,
      draw: envelope,
      col2: '#FFD166',
      size: [13, 18],
      life: [1, 1.3],
      gravity: 700,
      drag: 0.7,
      flip: [3, 6],
      vr: [-3, 3],
    });
    light(s, s.x, s.y, 100, '#FFD166', 0.7, 0.5);
  },
  mega(s) {
    greatEnvelope(s);
    after(900, () =>
      rain(s, {
        n: 40,
        over: 1600,
        vy: [140, 260],
        draw: envelope,
        col2: '#FFD166',
        size: [16, 24],
        life: [2.2, 2.6],
        sway: [20, 50],
        flip: [1.5, 3.5],
        vr: [-1.5, 1.5],
        gravity: 60,
      }),
    );
  },
};

/* ===================================== Mahjong (both): tiles fly out in a fan ===================================== */

const TILE_INK = ['#E5484D', '#2F8F6A', '#3B6FD8'] as const;

function tileFan(
  s: Stage,
  x: number,
  y: number,
  n: number,
  speed: readonly [number, number],
  size: readonly [number, number],
  gapMs: number,
  spreadK = 1,
  life: readonly [number, number] = [1.6, 2],
): void {
  const m = budget(n);
  for (let i = 0; i < m; i++) {
    const a = -Math.PI + (Math.PI * (1 - spreadK)) / 2 + ((i + 0.5) / m) * Math.PI * spreadK;
    const v = between(speed[0], speed[1]);
    spawn({
      x,
      y,
      vx: Math.cos(a) * v,
      vy: Math.sin(a) * v,
      delay: (i * gapMs) / 1000,
      draw: tile,
      col: TILE_INK[i % 3] ?? '#E5484D',
      col2: TILE_INK[(i + 1) % 3] ?? '#2F8F6A',
      data: i % 4,
      size: between(size[0], size[1]),
      life: between(life[0], life[1]),
      gravity: 720,
      drag: 0.7,
      rot: a + Math.PI / 2,
      vr: between(-3, 3),
    });
  }
}

function tileHand(s: Stage): void {
  let thrown = false;
  addActor({
    dur: 2.2,
    front: true,
    draw(g, k, f) {
      const cx = f.W / 2;
      const cy = f.H * 0.62;
      const R = Math.min(f.W, f.H) * 0.32;
      const size = Math.min(f.W, f.H) * 0.032;
      const open = ease.back(ease.span(k, 0.05, 0.45));
      const a = 1 - ease.span(k, 0.92, 1);
      const sp = glowSprite('#FFD166');
      if (sp) {
        g.globalCompositeOperation = 'lighter';
        g.globalAlpha = 0.45 * a * open;
        g.drawImage(sp, cx - R * 1.3, cy - R * 1.6, R * 2.6, R * 2);
        g.globalCompositeOperation = 'source-over';
      }
      if (k >= 0.78) {
        if (!thrown) {
          thrown = true;
          for (let i = 0; i < 13; i++) {
            const an = UP + ((i - 6) / 6) * 1.25;
            spawn({
              x: cx + Math.cos(an) * R,
              y: cy + Math.sin(an) * R,
              vx: Math.cos(an) * 520,
              vy: Math.sin(an) * 520,
              draw: tile,
              col: TILE_INK[i % 3] ?? '#E5484D',
              col2: TILE_INK[(i + 1) % 3] ?? '#2F8F6A',
              data: i % 4,
              size,
              life: 1.4,
              gravity: 600,
              drag: 0.8,
              rot: an + Math.PI / 2,
              vr: between(-4, 4),
            });
          }
          const st: Stage = { ...s, x: cx, y: cy - R };
          glints(st, cx, cy - R * 0.6, 26, R * 0.6, ['#FFFFFF', '#FFD166']);
        }
        return;
      }
      g.globalAlpha = a;
      for (let i = 0; i < 13; i++) {
        const an = UP + ((i - 6) / 6) * 1.25 * open;
        const lift = Math.sin(f.t * 5 + i * 0.5) * 3;
        stamp(
          g,
          tile,
          cx + Math.cos(an) * R,
          cy + Math.sin(an) * R + lift,
          size,
          an + Math.PI / 2,
          TILE_INK[i % 3] ?? '#E5484D',
          TILE_INK[(i + 1) % 3] ?? '#2F8F6A',
          f.dpr,
          { data: i % 4 },
        );
      }
    },
  });
}

export const mahjong: Preset = {
  pal: ['#FFD166', '#2F8F6A', '#E5484D', '#F6F1E4'],
  light: '#FFF6E0',
  win(s) {
    tileFan(s, s.x, s.y, 7, [240, 380], [12, 15], 20, 0.7, [0.8, 1.05]);
    glints(s, s.x, s.y, 8, 30, ['#FFFFFF', '#FFD166']);
  },
  big(s) {
    tileFan(s, s.W / 2, s.H + 20, 28, [660, 840], [18, 24], 28);
    rain(s, {
      n: 90,
      over: 1400,
      vy: [60, 140],
      draw: twinkle,
      cols: ['#FFD166', '#FFE7A3', '#FFFFFF'],
      size: [3, 6],
      life: [1.6, 2.2],
      vr: [-2, 2],
      add: true,
    });
    tileFan(s, s.x, s.y, 8, [260, 420], [13, 17], 15, 0.7);
  },
  mega(s) {
    tileHand(s);
    after(1700, () => tileFan(s, s.W / 2, s.H + 20, 24, [660, 840], [18, 24], 22));
    wash(s.W / 2, s.H * 0.5, { col: '#FFD166', r: Math.max(s.W, s.H) * 0.5, dur: 3, alpha: 0.3 });
  },
};

/* ===================================== Golden Flower: golden plum blossoms ===================================== */

const PLUM = ['#FFD166', '#F2B33D', '#FFE7A3', '#F59AB8', '#F07AA8'] as const;

function greatBlossom(s: Stage): void {
  let burstDone = false;
  rays({
    x: (W) => W / 2,
    y: (H) => H * 0.45,
    r: (W, H) => Math.max(W, H) * 0.45,
    cols: ['#FFD166', '#F59AB8'],
    n: 15,
    dur: 3,
    delay: 0.2,
    alpha: 0.12,
    spin: 0.2,
  });
  addActor({
    dur: 3,
    front: true,
    draw(g, k, f) {
      const cx = f.W / 2;
      const cy = f.H * 0.45;
      const R = Math.min(f.W, f.H) * 0.24;
      const a = ease.env(k, 0.06, 0.78);
      const spin = f.t * 0.3;
      g.globalAlpha = a;
      for (let i = 0; i < 5; i++) {
        const open = ease.back(ease.span(k, 0.04 + i * 0.05, 0.3 + i * 0.05));
        const an = (i / 5) * TAU - Math.PI / 2 + spin;
        g.fillStyle = i % 2 ? '#FFD166' : '#F2B33D';
        g.beginPath();
        g.arc(
          cx + Math.cos(an) * R * 0.62 * open,
          cy + Math.sin(an) * R * 0.62 * open,
          Math.max(0.5, R * 0.4 * open),
          0,
          TAU,
        );
        g.fill();
        g.fillStyle = '#FFF6E0';
        g.globalAlpha = a * 0.35;
        g.beginPath();
        g.arc(
          cx + Math.cos(an) * R * 0.62 * open - R * 0.1,
          cy + Math.sin(an) * R * 0.62 * open - R * 0.12,
          Math.max(0.3, R * 0.14 * open),
          0,
          TAU,
        );
        g.fill();
        g.globalAlpha = a;
      }
      const c = ease.span(k, 0.25, 0.4);
      g.fillStyle = '#C9773A';
      g.beginPath();
      g.arc(cx, cy, R * 0.26 * c, 0, TAU);
      g.fill();
      g.strokeStyle = '#FFE7A3';
      g.lineWidth = 2;
      for (let i = 0; i < 9; i++) {
        const an = (i / 9) * TAU + spin;
        const len = R * 0.45 * c;
        g.beginPath();
        g.moveTo(cx, cy);
        g.lineTo(cx + Math.cos(an) * len, cy + Math.sin(an) * len);
        g.stroke();
        g.fillStyle = '#FFF6E0';
        g.beginPath();
        g.arc(cx + Math.cos(an) * len, cy + Math.sin(an) * len, 2.5 * c, 0, TAU);
        g.fill();
      }
      if (!burstDone && k > 0.38) {
        burstDone = true;
        const st: Stage = { ...s, x: cx, y: cy };
        burst(st, cx, cy, {
          n: 60,
          r: R * 0.8,
          speed: [180, 460],
          draw: [plum, petal],
          cols: PLUM,
          col2: '#C9773A',
          size: [6, 11],
          life: [1.6, 2.2],
          gravity: 160,
          drag: 0.5,
          vr: [-3, 3],
        });
        glints(st, cx, cy, 24, R, ['#FFFFFF', '#FFD166']);
      }
    },
  });
}

export const zhajinhua: Preset = {
  pal: PLUM,
  light: '#FFF6E0',
  win(s) {
    burst(s, s.x, s.y, {
      n: 14,
      speed: [100, 260],
      angle: UP,
      spread: Math.PI * 1.6,
      draw: plum,
      col2: '#C9773A',
      size: [6, 9],
      life: [0.75, 0.95],
      gravity: 260,
      drag: 0.5,
      vr: [-3, 3],
    });
    burst(s, s.x, s.y, {
      n: 10,
      speed: [80, 220],
      angle: UP,
      spread: Math.PI * 1.6,
      draw: petal,
      col2: '#FFFFFF',
      size: [3, 5],
      life: [0.7, 0.9],
      gravity: 260,
      drag: 0.5,
      flip: [5, 9],
    });
    glints(s, s.x, s.y, 6, 26, ['#FFFFFF', '#FFD166']);
  },
  big(s) {
    rain(s, {
      n: 90,
      over: 1400,
      vy: [70, 140],
      vx: [-10, 30],
      draw: plum,
      col2: '#C9773A',
      size: [7, 11],
      life: [2.2, 2.8],
      sway: [20, 40],
      swayF: [2, 3],
      vr: [-1.5, 1.5],
      gravity: 30,
    });
    rain(s, {
      n: 90,
      over: 1400,
      vy: [70, 140],
      draw: petal,
      col2: '#FFFFFF',
      size: [3, 6],
      life: [2, 2.6],
      sway: [20, 40],
      flip: [3, 6],
      vr: [-2, 2],
      gravity: 30,
    });
    burst(s, s.x, s.y, {
      n: 22,
      speed: [160, 380],
      angle: UP,
      spread: Math.PI * 1.6,
      draw: plum,
      col2: '#C9773A',
      size: [7, 10],
      life: [1, 1.3],
      gravity: 280,
      drag: 0.5,
      vr: [-3, 3],
    });
    light(s, s.x, s.y, 100, '#FFD166', 0.7, 0.5);
  },
  mega(s) {
    greatBlossom(s);
    rain(s, {
      n: 100,
      over: 2400,
      vy: [70, 140],
      draw: [plum, petal],
      col2: '#C9773A',
      size: [5, 10],
      life: [2.2, 2.8],
      sway: [20, 40],
      vr: [-1.5, 1.5],
      flip: [0, 4],
      gravity: 30,
    });
  },
};

/* ===================================== Doudizhu: bomb 炸弹 and rocket 王炸 salutes ===================================== */

const DDZ = ['#E5484D', '#FFD166', '#FF8A3D', '#5B7CFA', '#FFFFFF'] as const;

function bomb(s: Stage, x: number, y: number, k = 1): void {
  shock(x, y, { col: '#FFF3D6', r: 130 * k, dur: 0.6, width: 5 });
  shock(x, y, { col: '#FF8A3D', r: 90 * k, dur: 0.7, width: 3, delay: 0.06 });
  explode(s, x, y, { col: '#FF8A3D', n: Math.round(70 * k), speed: 360 * k, sound: 'common/boom' });
  for (let i = 0; i < 3; i++)
    spawn({
      x: x + between(-20, 20),
      y: y + between(-10, 10),
      vx: between(-30, 30),
      vy: -between(10, 40),
      draw: puff,
      col: '#7C8AA3',
      size: 28 * k,
      grow: 1.2,
      life: 1.2,
      alpha: 0.22,
      fadeIn: 0.1,
    });
}

export const doudizhu: Preset = {
  pal: DDZ,
  light: '#FFF3D6',
  win(s) {
    bomb(s, s.x, s.y, 0.55);
  },
  big(s) {
    for (let i = 0; i < 3; i++)
      after(i * 380, () =>
        bomb(s, s.W * (0.25 + 0.25 * i) + between(-60, 60), s.H * between(0.25, 0.45), 1.15),
      );
    rain(s, {
      n: 110,
      over: 1400,
      vy: [140, 240],
      draw: paper,
      size: [5, 8],
      life: [2, 2.4],
      sway: [15, 30],
      flip: [5, 10],
      vr: [-3, 3],
      gravity: 80,
    });
  },
  mega(s) {
    // 王炸: the two jokers rise as two rockets and burst together
    const tx = s.W / 2;
    const ty = s.H * 0.3;
    for (const [i, col] of ['#E5484D', '#FFD166'].entries()) {
      launch(
        s,
        s.W * (0.3 + 0.4 * i),
        s.H + 10,
        tx + (i ? 30 : -30),
        ty,
        col,
        (x, y) => {
          explode(s, x, y, {
            col,
            n: 120,
            speed: 520,
            ring: true,
            pieces: { n: 18, draw: twinkle, cols: ['#FFFFFF', col], size: [4, 7] },
          });
          if (i === 1) {
            shock(tx, ty, { col: '#FFFFFF', r: Math.max(s.W, s.H) * 0.5, dur: 1.2, width: 6 });
            wash(tx, ty, { col: '#FFD166', r: Math.max(s.W, s.H) * 0.5, dur: 1.8, alpha: 0.4 });
            rain(s, {
              n: 130,
              over: 1200,
              vy: [140, 240],
              draw: paper,
              size: [5, 8],
              life: [2, 2.4],
              sway: [15, 30],
              flip: [5, 10],
              vr: [-3, 3],
              gravity: 80,
            });
          }
        },
        120 + i * 140,
      );
    }
    after(1500, () => {
      bomb(s, s.W * 0.22, s.H * 0.42, 1);
      bomb(s, s.W * 0.78, s.H * 0.42, 1);
    });
  },
};
